import _ from 'lodash';
import * as React from 'react';
import LaunchIcon from '@mui/icons-material/Launch';

import {
  Appointment,
  Container,
  LegacyContainerStatus,
  ReturnAppointmentsDictionary,
} from 'types/Watchlist';
import { formatDate } from 'utils/common';
import { useSegment } from 'common/hooks/segment';
import { FeatureFlag, useFeatureFlag } from 'utils/permissions/feature-flags';
import DisplayAppointmentsModal from '../../../../DisplayAppointmentsModal';
import { DATE_DISPLAY_FORMAT, DEFAULT_HOUR_MINUTE_DISPLAY } from '../../../../config';
import { ContainerAppointmentsContext, ReturnAppointmentsContext } from '../../context';
import { Cell } from '../Cell';
import { EmptyCell } from '../EmptyCell';
import { CellProps } from '../types';
import { displayTerminalPort, getAppointmentStatus, parseGateDates } from '../utility';
import { DateRange } from '../DateRange';
import { Scheduled } from '../pickup-cell/Scheduled';

export function getAvailableAppointments(
  container: Container,
  appointmentsByLineSize: ReturnAppointmentsDictionary
) {
  const { shippingLineKey, containerTypeKey } = container;
  if (_.isNil(shippingLineKey) || _.isNil(containerTypeKey)) {
    return [];
  }

  const appointmentsForLine = appointmentsByLineSize[`${shippingLineKey}${containerTypeKey}`];

  if (!appointmentsForLine) {
    return [];
  }

  const appointments = appointmentsForLine.sort((a, b) => a.periodFromDate.diff(b.periodFromDate));
  return appointments;
}

export const ReturnCell: React.FC<CellProps> = (props) => {
  const container: Container = props.row;

  const { gatedIn, status } = container;

  const { appointments: rawAppointments } = React.useContext(ReturnAppointmentsContext);
  const { appointments: containerAppointments } = React.useContext(ContainerAppointmentsContext);

  const { gatedInDate } = parseGateDates(container);

  const availableReturnAppointments = getAvailableAppointments(container, rawAppointments);

  const [appointmentsModalOpen, setAppointmentsModalOpen] = React.useState(false);
  const { trackEventNoSlack } = useSegment();
  const displayReturnBookedAppointments = useFeatureFlag(
    FeatureFlag.DisplayReturnBookedAppointments
  );

  const handleAppointmentModalOpen = React.useCallback(() => {
    trackEventNoSlack('All Appointments Modal Opened', {
      size: container.containerType,
      status: container.status,
      shippingLine: container.shippingLine,
      containerNumber: container.containerNumber,
    });

    setAppointmentsModalOpen(true);
  }, [container, setAppointmentsModalOpen, trackEventNoSlack]);

  if (
    status === null ||
    status === LegacyContainerStatus.Import ||
    status === LegacyContainerStatus.OnVessel ||
    status === LegacyContainerStatus.Loading ||
    status === LegacyContainerStatus.Pending
  ) {
    return <EmptyCell />;
  }

  if (gatedInDate) {
    const date = formatDate(
      gatedInDate,
      `${DATE_DISPLAY_FORMAT} - ${DEFAULT_HOUR_MINUTE_DISPLAY} z`
    );
    return (
      <Cell className="space-y-2 items-center text-center">
        <h5 className="text-gray-800 font-semibold">
          {displayTerminalPort(gatedIn?.terminalName, gatedIn?.portName)}
        </h5>
        <div className="space-y-1">
          <h6 className="uppercase">Returned</h6>
          <h6>{date}</h6>
        </div>
      </Cell>
    );
  }
  const appointment: Appointment | undefined = containerAppointments[container.containerNumber];
  if (displayReturnBookedAppointments && appointment?.returnBookedAppointment) {
    const { returnBookedAppointment } = appointment;
    const appointmentInfo = getAppointmentStatus(container, appointment, 'returnBookedAppointment');

    if (appointmentInfo.status === 'booked') {
      return (
        <Cell className="space-y-1 flex flex-col items-center text-center">
          <h5>{displayTerminalPort(returnBookedAppointment?.terminalName, gatedIn?.portName)}</h5>
          <Scheduled appointmentInfo={appointmentInfo} />
        </Cell>
      );
    }
  }

  const firstAppointment = _.head(availableReturnAppointments);
  if (availableReturnAppointments.length === 0 || !firstAppointment) {
    return (
      <Cell className="flex justify-center text-center">
        <h6 className="uppercase text-red-900 font-medium">No appt. available for singles</h6>
      </Cell>
    );
  }

  // TODO Check for an existing appointment

  return (
    <Cell className="space-y-2 items-center text-center">
      <a
        href={firstAppointment.terminalAppointmentUrl}
        target="_blank"
        className="flex items-center justify-center underline text-blue-900 space-x-1"
        rel="noreferrer"
      >
        <h6 className="uppercase font-medium">Book Appt.</h6>
        <LaunchIcon style={{ fontSize: 10 }} />
      </a>
      <h6 className="uppercase font-medium">First Available</h6>
      <div>
        <div className="flex justify-center space-x-1">
          <DateRange start={firstAppointment.periodFromDate} end={firstAppointment.periodToDate} />
          <h6>{firstAppointment.terminalName}</h6>
        </div>
        {availableReturnAppointments.length > 1 && (
          <>
            <h6
              onClick={handleAppointmentModalOpen}
              className="underline text-blue-900 cursor-pointer"
            >
              See others
            </h6>
            <DisplayAppointmentsModal
              open={appointmentsModalOpen}
              onClose={() => setAppointmentsModalOpen(false)}
              containerStatus={container.status}
              containerType={container.containerType}
              shippingLine={container.shippingLine}
              containerNumber={container.containerNumber}
              appointments={availableReturnAppointments}
              title="All return appointments for"
            />
          </>
        )}
      </div>
    </Cell>
  );
};
